import { useEffect, useRef, useState } from 'react';

const DEFAULT_MIN_LOADING_MS = 600;

/**
 * Keep loading indicator visible for at least the configured duration
 * to prevent UI jump/flicker for fast actions.
 */
export const useMinLoading = (
  loading: boolean,
  minDuration = DEFAULT_MIN_LOADING_MS,
): boolean => {
  const [visibleLoading, setVisibleLoading] = useState(loading);
  const startedAtRef = useRef<number | null>(loading ? Date.now() : null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (loading) {
      startedAtRef.current = Date.now();
      setVisibleLoading(true);
      return;
    }

    if (!visibleLoading) return;

    const startedAt = startedAtRef.current ?? Date.now();
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, minDuration - elapsed);

    if (remaining === 0) {
      startedAtRef.current = null;
      setVisibleLoading(false);
      return;
    }

    timer = setTimeout(() => {
      startedAtRef.current = null;
      setVisibleLoading(false);
    }, remaining);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [loading, minDuration, visibleLoading]);

  return visibleLoading;
};

